import axios from 'axios';
import tokenRefreshService from './tokenRefreshService';

/**
 * API Interceptor - Handles automatic token refresh on 401 responses
 * Implements transparent token refresh without user interaction
 */

// Track requests that are being retried to avoid infinite loops
const retryTracker = new Map();
const MAX_RETRIES = 1;

/**
 * Initialize axios interceptors for token refresh
 * Should be called once after MSAL is initialized
 * 
 * @param {AxiosInstance} axiosInstance - The axios instance to configure
 */
export const setupApiInterceptors = (axiosInstance) => {
  /**
   * Request Interceptor - Ensures token is fresh before request
   * Proactively refreshes token if it's about to expire
   */
  axiosInstance.interceptors.request.use(
    async (config) => {
      try {
        // Get current valid token
        const token = await tokenRefreshService.getValidToken();

        // Add token to request headers
        config.headers.Authorization = `Bearer ${token}`;

        console.log(`📤 Request to ${config.url} - Token attached`);
      } catch (error) {
        console.error('⚠ Request interceptor error:', error.message);
        console.log('ℹ Proceeding without token - API will return 401 if unauthorized');
        // Let the request go through anyway, response interceptor will handle 401
        // This allows us to retry with a refreshed token
      }

      return config;
    },
    (error) => {
      console.error('❌ Request interceptor failed:', error);
      return Promise.reject(error);
    }
  );

  /**
   * Response Interceptor - Handles 401 responses with automatic token refresh
   * Retries the original request after token refresh
   */
  axiosInstance.interceptors.response.use(
    (response) => {
      // Success - clear any retry tracking for this request
      const requestKey = `${response.config.method} ${response.config.url}`;
      retryTracker.delete(requestKey);

      console.log(`📥 Response from ${response.config.url} - Status: ${response.status}`);
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Only handle 401 (Unauthorized) errors
      if (error.response?.status !== 401) {
        console.error(`❌ API Error: ${error.response?.status || error.message}`);
        return Promise.reject(error);
      }

      console.warn(`⚠ 401 Unauthorized - Attempting token refresh...`);

      // Create a unique key for this request
      const requestKey = `${originalRequest.method} ${originalRequest.url}`;

      // Check if we've already retried this request
      const retryCount = retryTracker.get(requestKey) || 0;

      if (retryCount >= MAX_RETRIES) {
        console.error('❌ Max retries exceeded for request:', requestKey);
        retryTracker.delete(requestKey);
        return Promise.reject(new Error('Token refresh failed - max retries exceeded'));
      }

      try {
        // Increment retry count
        retryTracker.set(requestKey, retryCount + 1);

        // Attempt silent token refresh
        const newToken = await tokenRefreshService.refreshTokenSilent();

        console.log('✓ Token refreshed successfully');

        // Update authorization header with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        // Retry the original request
        console.log(`🔄 Retrying request: ${originalRequest.method} ${originalRequest.url}`);
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('✗ Token refresh failed:', refreshError.message);
        retryTracker.delete(requestKey);

        // If refresh fails, reject with appropriate error
        return Promise.reject(
          new Error(`Authentication failed: ${refreshError.message}`)
        );
      }
    }
  );

  console.log('✓ API interceptors configured for automatic token refresh');
};

export default setupApiInterceptors;
